/**
 * Atoll-aware fetch functions for all Atoll-compliant frontends.
 *
 * Provides HTTP request execution with built-in:
 * - JWT Bearer authentication (reads token via getJwtToken)
 * - 401/403 interception with automatic token refresh and retry
 * - Error surfacing to the shared console (dispatches to consoleSlice)
 * - Configurable response parsing (JSON, text, blob)
 *
 * Dependency contract: the consuming project's Redux store must include
 * authSlice and consoleSlice. The dispatch function is passed in via
 * AJAXSettings.
 *
 * Moved from helpers.tsx — cleaned up: removed React hooks violation
 * (useConsole), removed offline cache layer (Sputnik legacy), uses
 * consoleSlice dispatch directly.
 */

import { AJAXSettings } from "../types/ajaxSettings";
import { fnJSONParse, getJwtToken } from "../services/shared";
// renewUserSession replaced by refreshSession (OAuth2 path, not Sputnik envelope)
import { refreshSession, ensureFreshToken, fnUserLoggedOut } from "./atollAuth";
import { logout, setLoginOpen } from "./redux/authSlice";
import { addLog, Severity } from "./redux/consoleSlice";
import { authRejected, setLoginRequired } from "./redux/authDebugSlice";


function logError(dispatch: any, ...args: any[]) {
  const message = args.map(a =>
    typeof a === 'string' ? a : a instanceof Error ? a.message : JSON.stringify(a)
  ).join(' ');
  if (dispatch) {
    try {
      dispatch(addLog({ severity: Severity.Error, message }));
    } catch {
      console.error(message);
    }
  } else {
    console.error(message);
  }
}


/**
 * Callback-based fetch with Atoll auth integration.
 *
 * On 401/403, attempts token refresh via refreshSession and retries
 * the original request. On total auth failure, opens the login dialog.
 * Errors are surfaced to the shared console via consoleSlice.
 */
export async function fnAJAXFetch(ptAjaxSettings: AJAXSettings) {
  // Preemptive refresh when the user JWT is near/past expiry. Skipped when
  // the caller supplied their own Authorization (service-to-service flows).
  if (!ptAjaxSettings.authorization) {
    await ensureFreshToken(ptAjaxSettings.dispatch);
  }
  const headers: Record<string, string> = {
    'Content-Type': ptAjaxSettings.contentType || '',
    'Authorization': ptAjaxSettings.authorization || `Bearer ${getJwtToken()}`,
    'Accept': ptAjaxSettings.returnContentType === 'text' ? 'text/plain, */*;q=0.8'
        : ptAjaxSettings.returnContentType === 'blob' ? 'text/html, text/plain, */*;q=0.8'
        : 'application/json, text/plain, */*;q=0.8'
  };

  if (ptAjaxSettings.customHeaders) {
    Object.assign(headers, ptAjaxSettings.customHeaders);
  }

  var tRequestInfo: RequestInit = {
      method: ptAjaxSettings.method,
      mode: ptAjaxSettings.mode,
      cache: ptAjaxSettings.cache,
      credentials: ptAjaxSettings.credentials,
      headers: headers,
      redirect: ptAjaxSettings.redirect,
      referrerPolicy: ptAjaxSettings.referrerPolicy,
      body: ['GET', 'HEAD'].includes(ptAjaxSettings.method || 'GET') ? undefined : JSON.stringify(ptAjaxSettings.data)
  };

  try {
    const MAX_RETRIES = ptAjaxSettings.authorization ? 1 : 2;
    let attempt = 0;

    while (attempt < MAX_RETRIES) {
      attempt++;
      try {
        var oAJAXResponse = await fetch(ptAjaxSettings.url, tRequestInfo);

        const oData = await (async () => {
          switch (ptAjaxSettings.returnContentType) {
            case 'text':
              return await oAJAXResponse.text();
            case 'blob':
              return await oAJAXResponse.blob();
            default:
              const sContent = await oAJAXResponse.text();
              try {
                return fnJSONParse(sContent);
              } catch (eError) {
                logError(ptAjaxSettings.dispatch, eError, sContent);
                return sContent;
              }
            }
          })();

          if ([401, 403].includes(oAJAXResponse.status)) {
            if (ptAjaxSettings.dispatch) {
              try { ptAjaxSettings.dispatch(authRejected()); } catch {}
            }
            const refreshed = await refreshSession(ptAjaxSettings.dispatch);
            if (!refreshed.success) {
              fnUserLoggedOut();
              if (ptAjaxSettings.dispatch) {
                ptAjaxSettings.dispatch(logout());
                ptAjaxSettings.dispatch(setLoginOpen(true));
                try { ptAjaxSettings.dispatch(setLoginRequired(true)); } catch {}
              }
              attempt = MAX_RETRIES;
              throw new Error('Session renewal failed');
            }
            // Tokens already updated by refreshSession — rebuild headers with new token
            tRequestInfo.headers = {
                'Content-Type': ptAjaxSettings.contentType || '',
                'Authorization': ptAjaxSettings.authorization || `Bearer ${getJwtToken()}`,
                'Accept': ptAjaxSettings.returnContentType == 'text' ? 'text/plain, */*;q=0.8'
                    : ptAjaxSettings.returnContentType == 'blob' ? 'text/html, text/plain, */*;q=0.8'
                    : 'application/json, text/plain, */*;q=0.8'
              };
            continue;
          }

          if (ptAjaxSettings?.outcome_callback) {
            ptAjaxSettings.outcome_callback(oAJAXResponse.ok, oAJAXResponse.status, oData, oAJAXResponse.headers, ptAjaxSettings.callerID);
          } else {
            console.log(`no outcome_callback function defined: ${oAJAXResponse.ok},${oAJAXResponse.status}, ${oData}`);
          }
          return;

        } catch (error) {
          if (attempt >= MAX_RETRIES) {
            throw error;
          }
        }
    }

    if (ptAjaxSettings.dispatch) {
      try {
        ptAjaxSettings.dispatch(setLoginOpen(true));
      } catch (oErr) {
        console.log('Cannot dispatch');
      }
    }

  } catch (eError) {
    logError(ptAjaxSettings.dispatch, eError);
    if (ptAjaxSettings?.outcome_callback) {
      ptAjaxSettings.outcome_callback(false, 500, eError, {}, ptAjaxSettings.callerID);
    } else {
      console.log(`no outcome_callback function defined: false, 500, ${eError}`);
    }
  }
}


/**
 * Promise-based fetch with Atoll auth integration.
 *
 * Same auth behaviour as fnAJAXFetch but returns a promise instead of
 * using a callback. Returns { success, status, data, headers }.
 */
export async function fnAJAXFetchPromise(ptAjaxSettings: AJAXSettings): Promise<any> {
  if (!ptAjaxSettings.authorization) {
    await ensureFreshToken(ptAjaxSettings.dispatch);
  }
  var tRequestInfo: RequestInit = {
      method: ptAjaxSettings.method,
      mode: ptAjaxSettings.mode,
      cache: ptAjaxSettings.cache,
      credentials: ptAjaxSettings.credentials,
      headers: {
        'Content-Type': ptAjaxSettings.contentType || '',
        'Authorization': ptAjaxSettings.authorization || `Bearer ${getJwtToken()}`,
        'Accept': ptAjaxSettings.returnContentType == 'text' ? 'text/plain, */*;q=0.8'
            : ptAjaxSettings.returnContentType == 'blob' ? 'text/html, text/plain, */*;q=0.8'
            : 'application/json, text/plain, */*;q=0.8'
      },
      redirect: ptAjaxSettings.redirect,
      referrerPolicy: ptAjaxSettings.referrerPolicy,
      body: ['GET', 'HEAD'].includes(ptAjaxSettings.method || 'GET') ? undefined : JSON.stringify(ptAjaxSettings.data)
  };

  try {
    const MAX_RETRIES = ptAjaxSettings.authorization ? 1 : 2;
    let attempt = 0;

    while (attempt < MAX_RETRIES) {
      attempt++;
      try {
        const oAJAXResponse = await fetch(ptAjaxSettings.url, tRequestInfo);

        if ([401, 403].includes(oAJAXResponse.status)) {
          if (ptAjaxSettings.dispatch) {
            try { ptAjaxSettings.dispatch(authRejected()); } catch {}
          }
          const refreshed = await refreshSession(ptAjaxSettings.dispatch);
          if (!refreshed.success) {
            fnUserLoggedOut();
            if (ptAjaxSettings.dispatch) {
              ptAjaxSettings.dispatch(logout());
              ptAjaxSettings.dispatch(setLoginOpen(true));
              try { ptAjaxSettings.dispatch(setLoginRequired(true)); } catch {}
            }
            attempt = MAX_RETRIES;
            throw new Error('Session renewal failed');
          }
          tRequestInfo.headers = {
              'Content-Type': ptAjaxSettings.contentType || '',
              'Authorization': ptAjaxSettings.authorization || `Bearer ${getJwtToken()}`,
              'Accept': ptAjaxSettings.returnContentType == 'text' ? 'text/plain, */*;q=0.8'
                  : ptAjaxSettings.returnContentType == 'blob' ? 'text/html, text/plain, */*;q=0.8'
                  : 'application/json, text/plain, */*;q=0.8'
            };
          continue;
        }

        let responseData;
        switch (ptAjaxSettings.returnContentType) {
          case 'text':
            responseData = await oAJAXResponse.text();
            break;
          case 'blob':
            responseData = await oAJAXResponse.blob();
            break;
          default:
            const responseText = await oAJAXResponse.text();
            try {
              responseData = JSON.parse(responseText);
            } catch (eError) {
              responseData = responseText;
            }
            break;
        }

        return {
          success: oAJAXResponse.ok,
          status: oAJAXResponse.status,
          data: responseData,
          headers: oAJAXResponse.headers
        };
      } catch (error) {
        if (attempt >= MAX_RETRIES) {
          throw error;
        }
      }
    }
  } catch (eError) {
    return { success: false, status: 500, data: eError };
  }
}


/**
 * Streaming fetch with Atoll auth integration.
 *
 * Same auth behaviour as fnAJAXFetchPromise but returns the raw
 * Response object so callers can stream the body (ReadableStream,
 * SSE line parsing, etc.). The caller is responsible for consuming
 * and closing the response body.
 *
 * Supports an optional rawBody parameter for non-JSON payloads
 * (FormData, Blob, etc.). When rawBody is provided, Content-Type
 * is omitted so the browser can set it automatically (e.g. with
 * the multipart boundary for FormData).
 */
export async function fnAJAXFetchStream(
  ptAjaxSettings: AJAXSettings,
  rawBody?: BodyInit,
): Promise<Response> {
  const useRawBody = rawBody !== undefined;

  if (!ptAjaxSettings.authorization) {
    await ensureFreshToken(ptAjaxSettings.dispatch);
  }

  function buildHeaders(): Record<string, string> {
    const h: Record<string, string> = {
      'Authorization': ptAjaxSettings.authorization || `Bearer ${getJwtToken()}`,
    };
    if (!useRawBody) {
      h['Content-Type'] = ptAjaxSettings.contentType || '';
    }
    if (ptAjaxSettings.customHeaders) {
      Object.assign(h, ptAjaxSettings.customHeaders);
    }
    return h;
  }

  var tRequestInfo: RequestInit = {
      method: ptAjaxSettings.method,
      mode: ptAjaxSettings.mode,
      cache: ptAjaxSettings.cache,
      credentials: ptAjaxSettings.credentials,
      headers: buildHeaders(),
      redirect: ptAjaxSettings.redirect,
      referrerPolicy: ptAjaxSettings.referrerPolicy,
      body: ['GET', 'HEAD'].includes(ptAjaxSettings.method || 'GET')
        ? undefined
        : useRawBody
          ? rawBody
          : JSON.stringify(ptAjaxSettings.data),
  };

  const MAX_RETRIES = ptAjaxSettings.authorization ? 1 : 2;
  let attempt = 0;

  while (attempt < MAX_RETRIES) {
    attempt++;
    const oAJAXResponse = await fetch(ptAjaxSettings.url, tRequestInfo);

    if ([401, 403].includes(oAJAXResponse.status)) {
      if (ptAjaxSettings.dispatch) {
        try { ptAjaxSettings.dispatch(authRejected()); } catch {}
      }
      const refreshed = await refreshSession(ptAjaxSettings.dispatch);
      if (!refreshed.success) {
        fnUserLoggedOut();
        if (ptAjaxSettings.dispatch) {
          ptAjaxSettings.dispatch(logout());
          ptAjaxSettings.dispatch(setLoginOpen(true));
          try { ptAjaxSettings.dispatch(setLoginRequired(true)); } catch {}
        }
        throw new Error('Session renewal failed');
      }
      tRequestInfo.headers = buildHeaders();
      continue;
    }

    return oAJAXResponse;
  }

  throw new Error('Max retries exceeded');
}
